import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Component, effect, inject, Injector, OnDestroy, OnInit } from '@angular/core';
import { LoaderService } from './core/services/loader.service';
import { LayoutComponent } from './layouts/main-layout/layout/layout.component';
import { NavService } from './core/services/nav.service';
import { EventTypes, OidcSecurityService, PublicEventsService } from 'angular-auth-oidc-client';
import { filter } from 'rxjs';
import { LoaderComponent } from './shared/components/loader/loader.component';

@Component({
  selector: 'app-root',
  imports: [LayoutComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  readonly loaderService = inject(LoaderService);
  private readonly navService = inject(NavService);
  readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly publicEventsService = inject(PublicEventsService);
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private overlayRef: OverlayRef | null = null;

  constructor() {
    effect(() => {
      if (this.loaderService.ongoingRequests() > 0) {
        this.attachLoaderOverlay();
        return;
      }

      this.detachLoaderOverlay();
    });
  }

  ngOnInit(): void {
    this.navService.clearState();
    // Following method needs to be called to start the complete authentication flow
    this.oidcSecurityService.checkAuth().subscribe(() => { });
    /*
      Following callback redirects user to perform login when silent renew fails.
      (When silent renew fails because of refresh token is expired, this will redirect user to perform login)
    */
    this.publicEventsService.registerForEvents()
      .pipe(filter((notification) => notification.type === EventTypes.SilentRenewFailed))
      .subscribe(() => {
        // Redirects user to perform login
        this.oidcSecurityService.authorize();
      });
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }

  private attachLoaderOverlay(): void {
    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        hasBackdrop: false,
        disposeOnNavigation: true,
        panelClass: 'loader-overlay-pane',
        positionStrategy: this.overlay.position().global().top('0').left('0'),
        scrollStrategy: this.overlay.scrollStrategies.block(),
        width: '100vw',
        height: '100vh',
      });
    }

    if (!this.overlayRef.hasAttached()) {
      this.overlayRef.attach(new ComponentPortal(LoaderComponent, null, this.injector));
    }
  }

  private detachLoaderOverlay(): void {
    if (this.overlayRef?.hasAttached()) {
      this.overlayRef.detach();
    }
  }
}
